import { createI18n } from "vue-i18n";
import { pathOr } from "ramda";

const localeFiles = import.meta.glob<Record<string, unknown>>(
  "@/modules/*/locale/*.json",
  { eager: true, import: "default" }
);
const globalLocaleFiles = import.meta.glob<Record<string, unknown>>(
  "@/locale/*.json",
  { eager: true, import: "default" }
);

const getLocaleFile = (filePath: string) => {
  const matchedEntry = Object.entries(localeFiles).find(([key]) =>
    key.endsWith(filePath)
  );
  return matchedEntry?.[1] || {};
};

const getGlobalLocaleFile = (fileName: string) => {
  const matchedEntry = Object.entries(globalLocaleFiles).find(([key]) =>
    key.endsWith(`/src/locale/${fileName}`)
  );
  return matchedEntry?.[1] || {};
};

const appLangs = {
  install(app: any, options: any) {
    const { currentRoute } = options;
    const projectName = import.meta.env.VUE_APP_PROJECT_NAME;
    let moduleLangs: Record<string, Record<string, unknown>> = {};

    if ("modules" in options) {
      moduleLangs = options["modules"].reduce(
        (prev: Record<string, Record<string, unknown>>, module: string) => {
          try {
            const moduleFaLang = getLocaleFile(
              `/src/modules/${module}/locale/${projectName}.json`
            );
            const fa = getLocaleFile(`/src/modules/${module}/locale/fa.json`);
            const en = getLocaleFile(`/src/modules/${module}/locale/en.json`);
            const ar = getLocaleFile(`/src/modules/${module}/locale/ar.json`);
            return {
              fa: { ...prev.fa, ...fa, ...moduleFaLang },
              en: { ...prev.en, ...en },
              ar: { ...prev.ar, ...ar },
            };
          } catch (err) {
            return prev;
          }
        },
        moduleLangs
      );
    }

    const globalModule = getGlobalLocaleFile(`${projectName}.json`);

    const globalFa = getGlobalLocaleFile("fa.json");
    const moduleFa = pathOr({}, ["fa"], moduleLangs);

    const globalEn = getGlobalLocaleFile("en.json");
    const moduleEn = pathOr({}, ["en"], moduleLangs);

    const globalAr = getGlobalLocaleFile("ar.json");
    const moduleAr = pathOr({}, ["ar"], moduleLangs);

    const messages: any = {
      fa: { ...globalFa, ...globalModule, ...moduleFa },
      en: { ...globalEn, ...moduleEn },
      ar: { ...globalAr, ...moduleAr },
    };

    const localLang = localStorage.getItem("localLang");
    const envLang = import.meta.env.VUE_APP_I18N_LOCALE;
    const locale = localLang || envLang || "fa";

    const Ii8n = createI18n({
      locale,
      globalInjection: true,
      fallbackLocale: import.meta.env.VUE_APP_I18N_FALLBACK_LOCALE || "fa",
      legacy: false,
      messages: messages,
    });

    app.use(Ii8n);

    const lang = (langKey: string, ...args: any) => {
      const { moduleName } = currentRoute.value.meta;
      const t = app.config.globalProperties.$t;
      const isModuleLang = !!pathOr(
        false,
        [locale, moduleName, langKey],
        messages
      );

      return isModuleLang
        ? t(`${moduleName}.${langKey}`, ...args)
        : t(langKey, ...args);
    };

    app.config.globalProperties.$lang = lang;
  },
};
export default appLangs;
